import { useEffect } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";

import appCss from "../styles.css?url";
import { pageview, scroll } from "@/lib/analytics";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-gray-950 transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-gray-950 transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-gray-950 transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

const SHORT_DESCRIPTION =
  "Conversion-focused websites built by a senior creative director. See your design before you pay — more leads, more sales, no agency runaround.";

const GA_ID = import.meta.env.VITE_GA_MEASUREMENT_ID ?? "";

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "Mind Media — Websites That Actually Make Money" },
      { name: "description", content: SHORT_DESCRIPTION },
      { name: "author", content: "Mind Media" },
      { property: "og:title", content: "Mind Media — Websites That Actually Make Money" },
      { property: "og:description", content: SHORT_DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "Mind Media" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Mind Media — Websites That Actually Make Money" },
      { name: "twitter:description", content: SHORT_DESCRIPTION },
      { property: "og:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/TKbZDoerARgDEskSr1NEyX7rjDC2/social-images/social-1781545532146-social_image.webp" },
      { name: "twitter:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/TKbZDoerARgDEskSr1NEyX7rjDC2/social-images/social-1781545532146-social_image.webp" },
    ],
    links: [
      {
        rel: "stylesheet",
        href: appCss,
      },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700;800&display=swap",
      },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap",
      },
      {
        rel: "preconnect",
        href: "https://story.shareloapp.com",
        crossOrigin: "anonymous",
      },
      {
        rel: "dns-prefetch",
        href: "https://story.shareloapp.com",
      },
    ],
    scripts: [
      ...(GA_ID
        ? [
            {
              async: true,
              src: `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`,
            },
            {
              type: "text/javascript",
              children: `
                window.dataLayer = window.dataLayer || [];
                function gtag(){dataLayer.push(arguments);}
                gtag('js', new Date());
                gtag('config', '${GA_ID}', { send_page_view: false });
              `,
            },
          ]
        : []),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Organization",
              "@id": "https://mindmedia.co/#organization",
              name: "Mind Media",
              url: "https://mindmedia.co",
              description: SHORT_DESCRIPTION,
              founder: { "@type": "Person", name: "Wendel Santos" },
            },
            {
              "@type": "WebSite",
              "@id": "https://mindmedia.co/#website",
              url: "https://mindmedia.co",
              name: "Mind Media",
              publisher: { "@id": "https://mindmedia.co/#organization" },
            },
          ],
        }),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  return (
    <QueryClientProvider client={queryClient}>
      <Outlet />
      <AnalyticsTracker />
    </QueryClientProvider>
  );
}

function AnalyticsTracker() {
  const router = useRouter();

  useEffect(() => {
    if (typeof window === "undefined") return;

    // Track the initial page view once the router is hydrated.
    pageview(router.state.location.href, document.title);

    const unsubscribe = router.subscribe("onResolved", () => {
      pageview(router.state.location.href, document.title);
    });

    // Scroll-depth tracking (50% and 90%) — reset on each route change.
    const fired = new Set<number>();
    const onScroll = () => {
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (docHeight <= 0) return;
      const percent = (window.scrollY / docHeight) * 100;
      for (const threshold of [50, 90] as const) {
        if (percent >= threshold && !fired.has(threshold)) {
          fired.add(threshold);
          scroll(threshold);
        }
      }
    };

    const resetScrollTracking = () => {
      fired.clear();
      onScroll();
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    const unsubscribeLocation = router.subscribe("onResolved", resetScrollTracking);

    return () => {
      unsubscribe();
      unsubscribeLocation();
      window.removeEventListener("scroll", onScroll);
    };
  }, [router]);

  return null;
}
